/**
 * GET /api/v1/me — the signed-in user.
 *
 * This is the single fetcher for `/me`. The Redux bootstrap thunk resolves
 * through the same query key, so app boot and any component calling
 * `useCurrentUser()` share one request and one cache entry.
 *
 * Redux `selectCurrUser` remains the read path for the ~228 files that already
 * use it; this hook is the migration target for new code and for surfaces being
 * moved off the store one at a time.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { http } from '@/lib/http';

export const currentUserQueryKey = ['currentUser'] as const;

export interface CurrentUser {
  id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  time_zone?: string;
  [key: string]: unknown;
}

/** Raw envelope the endpoint returns; the thunk reads `.data` off it. */
export interface CurrentUserResponse {
  data?: CurrentUser;
  errors?: string[];
  [key: string]: unknown;
}

export const fetchCurrentUserProfile = (): Promise<CurrentUserResponse> =>
  http.get('/me') as Promise<CurrentUserResponse>;

export const useCurrentUser = (): UseQueryResult<CurrentUser | undefined> =>
  useQuery({
    queryKey: currentUserQueryKey,
    queryFn: fetchCurrentUserProfile,
    // Boot has almost always populated this already; without a stale window the
    // hook would refetch on mount and reintroduce the second request this
    // exists to remove.
    staleTime: 5 * 60 * 1000,
    select: (response) => response?.data,
  });
